import {useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {useAuth} from '../../../../../../../contexts/auth/context';
import {RolePermissionType} from '../../../../../../../backend/enums/role-permission-type';
import {SeniorAttachmentsHeader} from './header/senior-attachments-header';
import {useSnackBar} from '../../../../../../../contexts/snack-bar/context';
import {SavingAttachment} from '../../../../../../../components/daily-care-log/attachments/add-attachments-form';
import {requestAttachmentStorageUrl} from '../../../../../daily-care-log/unplanned-tasks/unplannedtask.daily-care-log.screen';
import {StorageType} from '../../../../../../../backend/enums/storage-type';
import {EntityUserAttachmentsRepository} from '../../../../../../../repositories/entity-user-attachments';
import {Attachment, AttachmentDataFile} from '../../../../../../../backend/interfaces/attachment';
import {PatchAttachment} from '../../../../../../../backend/requests/patch-attachment';
import {downloadFile} from '../../../../../../../utils/functions/download-file';
import {translations} from '../../../../../../../i18n/translations';
import {SeniorAttachmentsCreateModal} from './modals/senior-attachments-create-modal';
import {SeniorAttachmentsEditModal} from './modals/senior-attachments-edit-modal';
import {SeniorAttachmentsDeleteModal} from './modals/senior-attachments-delete-modal';
import {useSeniorAttachmentsData} from './use-senior-attachments-data';
import {SeniorAttachmentsEmpty} from './body/senior-attachments-empty';
import {SeniorAttachmentsTable} from './body/senior-attachments-table/senior-attachments-table';

export default function SeniorAttachmentsProfessionalCaregiverOfficeScreen() {
  const {id, entityId}: {id?: string; entityId?: string} = useParams();
  const {hasWritePermission, state: authState} = useAuth();
  const snackbar = useSnackBar();
  const {t} = useTranslation();
  const [selectedAttachment, setSelectedAttachment] = useState<Attachment | undefined>(undefined);
  const [toggleCreateModal, setToggleCreateModal] = useState(false);
  const [toggleEditModal, setToggleEditModal] = useState(false);
  const [toggleDeleteModal, setToggleDeleteModal] = useState(false);

  const canWriteSeniorAttachments = hasWritePermission(
    RolePermissionType.Seniors_PersonalData_Attachments
  );

  // Load attachments data
  const data = useSeniorAttachmentsData();

  const handleCreateAttachments = async (savingAttachments: SavingAttachment[]) => {
    if (!entityId) throw new Error('Entity ID is required to save attachments.');
    if (!id) throw new Error('Senior ID is required to save attachments.');

    // Request storage URLs for the attachments
    const attachments = await requestAttachmentStorageUrl(
      entityId,
      savingAttachments,
      StorageType.CareTaskAttachment,
      authState.session?.currentUser.id
    );

    // Attachments upload failed.
    if (attachments === null) {
      throw new Error('Failed to upload attachments.');
    }

    // Attachments upload success. Add attachments to user.
    const newAttachments = await EntityUserAttachmentsRepository.instance.createAttachments(
      entityId,
      id,
      attachments
    );

    // Refresh the attachments list
    data.refresh(newAttachments);
  };

  const handleEditAttachment = async (updatedData: PatchAttachment) => {
    if (!entityId) throw new Error('Entity ID is required to save attachments.');
    if (!id) throw new Error('Senior ID is required to save attachments.');
    if (!selectedAttachment?.id) throw new Error('No attachment selected for editing.');

    // Update the attachment using the repository
    const updatedAttachment = await EntityUserAttachmentsRepository.instance.patchAttachment(
      entityId,
      id,
      selectedAttachment.id,
      updatedData
    );

    // Update the local data with the edited attachment
    data.refreshAttachment(updatedAttachment);
  };

  const handleDeleteAttachment = async () => {
    if (!entityId) throw new Error('Entity ID is required to delete attachments.');
    if (!id) throw new Error('Senior ID is required to delete attachments.');
    if (!selectedAttachment?.id) throw new Error('No attachment selected for deletion.');

    // Delete the attachment using the repository
    await EntityUserAttachmentsRepository.instance.deleteAttachment(
      entityId,
      id,
      selectedAttachment.id
    );

    // Remove the attachment from the local data
    data.removeAttachment(selectedAttachment.id);
  };

  const handleDownloadAttachment = async (attachment: Attachment, numTries: number = 1) => {
    let attachUrl = (attachment.data as AttachmentDataFile)?.fileUrl;
    if (!attachUrl) {
      console.error('Attachment does not have a valid file URL:', attachment);
      snackbar.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.user_attachments.error.download),
      });
      return;
    }

    try {
      await downloadFile(attachUrl, attachment.name || attachment.id);
    } catch (error) {
      if (!(error instanceof AxiosError) && error.status) throw error;

      // The url might be invalid or expired. We try again to get a new URL.
      if (error.status >= 400 || error.status < 500) {
        console.log(`Failed to download attachment, re-trying (${numTries})..`);
        // Give up
        if (numTries >= 3) {
          throw error;
        }

        // Refetch
        const newAttachments = await data.fetch();
        // Find
        const newAttachment = newAttachments?.find((a) => a.id === attachment.id);
        // Try again
        if (newAttachment) {
          await handleDownloadAttachment(newAttachment, numTries + 1);
        }
        // Fallback
        else {
          throw error;
        }
      }
    }
  };

  return (
    <div className="senior-attachments">
      {/* ------------- HEADER ------------- */}
      <SeniorAttachmentsHeader
        canWrite={canWriteSeniorAttachments}
        onCreateAttachmentClick={() => {
          setToggleCreateModal(true);
        }}
      />

      {/* ------------- BODY ------------- */}
      {data.attachments.length === 0 && !data.loading ? (
        /* --- ATTACHMENTS EMPTY --- */
        <SeniorAttachmentsEmpty />
      ) : (
        /* --- ATTACHMENTS TABLE --- */
        <SeniorAttachmentsTable
          loading={data.loading}
          canWrite={canWriteSeniorAttachments}
          attachments={data.attachments}
          authors={data.authorsCache}
          onAttachmentEditClick={(attachment) => {
            setToggleEditModal(true);
            setSelectedAttachment(attachment);
          }}
          onAttachmentDeleteClick={(attachment) => {
            setToggleDeleteModal(true);
            setSelectedAttachment(attachment);
          }}
          onAttachmentDownloadClick={async (attachment) => {
            try {
              // Handle
              await handleDownloadAttachment(attachment);
            } catch (error) {
              // Error
              console.error('Error downloading attachment:', error);
              snackbar.setOfficeToastNotification({
                severity: 'error',
                message: t(translations.user_attachments.error.download),
              });
            }
          }}
        />
      )}

      {/* ------------- MODALS ------------- */}
      {/* --- CREATE ATTACHMENT --- */}
      {toggleCreateModal && (
        <SeniorAttachmentsCreateModal
          open={toggleCreateModal}
          onClose={() => {
            setToggleCreateModal(false);
            setSelectedAttachment(undefined);
          }}
          onSave={async (savingAttachments) => {
            try {
              // Handle
              await handleCreateAttachments(savingAttachments);
              // Success
              setToggleCreateModal(false);
              snackbar.setOfficeToastNotification({
                severity: 'success',
                message: t(translations.user_attachments.success.create),
              });
            } catch (error) {
              // Error
              console.error('Error saving attachments:', error);
              snackbar.setOfficeToastNotification({
                severity: 'error',
                message: t(translations.user_attachments.error.create),
              });
            }
          }}
        />
      )}

      {/* --- EDIT ATTACHMENT --- */}
      {toggleEditModal && selectedAttachment && (
        <SeniorAttachmentsEditModal
          open={toggleEditModal}
          attachment={selectedAttachment}
          canWrite={canWriteSeniorAttachments}
          onClose={() => {
            setToggleEditModal(false);
            setSelectedAttachment(undefined);
          }}
          onSave={async (data) => {
            try {
              // Handle
              await handleEditAttachment(data);
              // Success
              setToggleEditModal(false);
              snackbar.setOfficeToastNotification({
                severity: 'success',
                message: t(translations.user_attachments.success.edit),
              });
            } catch (error) {
              // Error
              console.error('Error saving edited attachment:', error);
              snackbar.setOfficeToastNotification({
                severity: 'error',
                message: t(translations.user_attachments.error.edit),
              });
            }
          }}
        />
      )}

      {/* --- DELETE ATTACHMENT --- */}
      {toggleDeleteModal && selectedAttachment && (
        <SeniorAttachmentsDeleteModal
          open={toggleDeleteModal}
          attachment={selectedAttachment}
          onClose={() => {
            setToggleDeleteModal(false);
            setSelectedAttachment(undefined);
          }}
          onDelete={async () => {
            try {
              // Handle
              await handleDeleteAttachment();
              // Success
              setToggleDeleteModal(false);
              snackbar.setOfficeToastNotification({
                severity: 'success',
                message: t(translations.user_attachments.success.delete),
              });
            } catch (error) {
              // Error
              console.error('Error saving edited attachment:', error);
              snackbar.setOfficeToastNotification({
                severity: 'error',
                message: t(translations.user_attachments.error.delete),
              });
            }
          }}
        />
      )}
    </div>
  );
}
